import React, { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { 
  Calendar, 
  AlertTriangle, 
  Clock, 
  Users,
  Building2,
  RefreshCw,
  ChevronRight,
  Wrench,
  MapPin,
  CheckCircle2
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// Status colors
const STATUS_STYLES = {
  "Compliant": "bg-green-100 text-green-700",
  "Due Soon": "bg-amber-100 text-amber-700",
  "Overdue": "bg-red-100 text-red-700",
  "Failed": "bg-red-200 text-red-800",
};

export default function PPMWorkloadPage() {
  const [upcoming, setUpcoming] = useState(null);
  const [overdue, setOverdue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [daysFilter, setDaysFilter] = useState("30");
  const [activeTab, setActiveTab] = useState("upcoming");

  const loadData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };
      
      const [upcomingRes, overdueRes] = await Promise.all([
        axios.get(`${API}/api/ppm/workload/upcoming?days=${daysFilter}`, { headers }),
        axios.get(`${API}/api/ppm/workload/overdue`, { headers }),
      ]);
      
      setUpcoming(upcomingRes.data);
      setOverdue(overdueRes.data);
    } catch (err) {
      console.error("Failed to load workload:", err);
      toast.error("Failed to load PPM workload data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [daysFilter]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 bg-slate-50 min-h-screen" data-testid="ppm-workload-page">
      {/* Header */}
      <div className="rounded-2xl bg-gradient-to-r from-teal-700 to-teal-600 p-6 text-white">
        <h1 className="text-2xl font-bold" data-testid="page-title">PPM Workload</h1>
        <p className="text-teal-100 text-sm mt-1">Upcoming due items and overdue backlog</p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-l-4 border-l-teal-500" data-testid="upcoming-count-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <Calendar className="h-4 w-4" />
              Upcoming ({daysFilter}d)
            </div>
            <div className="text-3xl font-bold text-teal-700">{upcoming?.count || 0}</div>
          </CardContent>
        </Card>
        
        <Card className="border-l-4 border-l-red-500" data-testid="overdue-count-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <AlertTriangle className="h-4 w-4" />
              Overdue/Failed
            </div>
            <div className="text-3xl font-bold text-red-600">{overdue?.count || 0}</div>
          </CardContent>
        </Card>
        
        <Card className="border-l-4 border-l-blue-500" data-testid="contractors-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <Users className="h-4 w-4" />
              Contractors
            </div>
            <div className="text-3xl font-bold text-blue-600">
              {Object.keys(upcoming?.by_contractor || {}).length}
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-l-4 border-l-purple-500" data-testid="parks-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <Building2 className="h-4 w-4" />
              Parks
            </div>
            <div className="text-3xl font-bold text-purple-600">
              {Object.keys(upcoming?.by_park || {}).length}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4 flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4 text-slate-500" />
            <span className="text-sm text-slate-600">Show upcoming:</span>
          </div>
          <Select value={daysFilter} onValueChange={setDaysFilter}>
            <SelectTrigger className="w-32" data-testid="days-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">7 days</SelectItem>
              <SelectItem value="14">14 days</SelectItem>
              <SelectItem value="30">30 days</SelectItem>
              <SelectItem value="60">60 days</SelectItem>
              <SelectItem value="90">90 days</SelectItem>
            </SelectContent>
          </Select>
          
          <Button variant="ghost" size="sm" onClick={loadData} data-testid="refresh-btn">
            <RefreshCw className="h-4 w-4" />
          </Button>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2 max-w-md">
          <TabsTrigger value="upcoming" data-testid="upcoming-tab">
            Upcoming ({upcoming?.count || 0})
          </TabsTrigger>
          <TabsTrigger value="overdue" data-testid="overdue-tab">
            Overdue ({overdue?.count || 0})
          </TabsTrigger>
        </TabsList>

        {/* Upcoming Tab */}
        <TabsContent value="upcoming" className="space-y-4 mt-4">
          {/* Distribution Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* By Contractor */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Users className="h-4 w-4 text-teal-600" />
                  By Contractor
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 max-h-48 overflow-auto">
                  {Object.entries(upcoming?.by_contractor || {})
                    .sort((a, b) => b[1] - a[1])
                    .map(([contractor, count]) => (
                      <div key={contractor} className="flex items-center justify-between py-1">
                        <span className={`text-sm ${contractor === 'Unassigned' ? 'text-red-500 font-medium' : ''}`}>
                          {contractor}
                        </span>
                        <Badge variant="secondary">{count}</Badge>
                      </div>
                    ))}
                  {Object.keys(upcoming?.by_contractor || {}).length === 0 && (
                    <p className="text-sm text-slate-400 text-center py-4">No upcoming work</p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* By Park */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-teal-600" />
                  By Park
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 max-h-48 overflow-auto">
                  {Object.entries(upcoming?.by_park || {})
                    .sort((a, b) => b[1] - a[1])
                    .map(([park, count]) => (
                      <div key={park} className="flex items-center justify-between py-1">
                        <span className="text-sm">{park}</span>
                        <Badge variant="secondary">{count}</Badge>
                      </div>
                    ))}
                  {Object.keys(upcoming?.by_park || {}).length === 0 && (
                    <p className="text-sm text-slate-400 text-center py-4">No upcoming work</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Items List */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Upcoming Items</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 max-h-[500px] overflow-auto">
                {(upcoming?.rows || []).map((row) => (
                  <div 
                    key={row.schedule_id} 
                    className="border rounded-lg p-4 bg-white hover:shadow-sm transition-shadow"
                    data-testid={`upcoming-item-${row.schedule_id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <MapPin className="h-4 w-4 text-slate-400" />
                          <span className="font-medium">{row.park_name}</span>
                          <ChevronRight className="h-4 w-4 text-slate-300" />
                          <span className="text-slate-600">{row.unit_name}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <Wrench className="h-3 w-3 text-slate-400" />
                          <span>{row.template_name}</span>
                          <Badge className={STATUS_STYLES[row.status]}>{row.status}</Badge>
                        </div>
                        <div className="flex items-center gap-4 text-xs text-slate-500 mt-2">
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            Due: {row.next_due_date ? new Date(row.next_due_date).toLocaleDateString() : "-"}
                          </span>
                          <span>Trade: {row.trade_type}</span>
                        </div>
                      </div>
                      <div className="text-right text-sm">
                        {row.assigned_contractor_name ? (
                          <span className="text-slate-600">{row.assigned_contractor_name}</span>
                        ) : (
                          <Badge className="bg-red-100 text-red-700">Unassigned</Badge>
                        )}
                        {row.active_job_reference && (
                          <div className="text-xs text-teal-600 mt-1">{row.active_job_reference}</div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
                {(upcoming?.rows || []).length === 0 && (
                  <div className="text-center py-8 text-slate-400">
                    <Calendar className="h-12 w-12 mx-auto mb-2 opacity-50" />
                    <p>No upcoming PPM work in the next {daysFilter} days</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Overdue Tab */}
        <TabsContent value="overdue" className="space-y-4 mt-4">
          {/* Distribution Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* By Contractor */}
            <Card className="border-red-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2 text-red-700">
                  <Users className="h-4 w-4" />
                  Overdue by Contractor
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 max-h-48 overflow-auto">
                  {Object.entries(overdue?.by_contractor || {})
                    .sort((a, b) => b[1] - a[1])
                    .map(([contractor, count]) => (
                      <div key={contractor} className="flex items-center justify-between py-1">
                        <span className={`text-sm ${contractor === 'Unassigned' ? 'text-red-600 font-medium' : ''}`}>
                          {contractor}
                        </span>
                        <Badge className="bg-red-100 text-red-700">{count}</Badge>
                      </div>
                    ))}
                  {Object.keys(overdue?.by_contractor || {}).length === 0 && (
                    <p className="text-sm text-green-500 text-center py-4">No overdue items!</p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* By Park */}
            <Card className="border-red-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2 text-red-700">
                  <Building2 className="h-4 w-4" />
                  Overdue by Park
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 max-h-48 overflow-auto">
                  {Object.entries(overdue?.by_park || {})
                    .sort((a, b) => b[1] - a[1])
                    .map(([park, count]) => (
                      <div key={park} className="flex items-center justify-between py-1">
                        <span className="text-sm">{park}</span>
                        <Badge className="bg-red-100 text-red-700">{count}</Badge>
                      </div>
                    ))}
                  {Object.keys(overdue?.by_park || {}).length === 0 && (
                    <p className="text-sm text-green-500 text-center py-4">No overdue items!</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Overdue Items List */}
          <Card className="border-red-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-base text-red-700">Overdue / Failed Items</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 max-h-[500px] overflow-auto">
                {(overdue?.rows || []).map((row) => (
                  <div 
                    key={row.schedule_id} 
                    className="border border-red-200 rounded-lg p-4 bg-red-50 hover:shadow-sm transition-shadow"
                    data-testid={`overdue-item-${row.schedule_id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <AlertTriangle className="h-4 w-4 text-red-500" />
                          <span className="font-medium">{row.park_name}</span>
                          <ChevronRight className="h-4 w-4 text-slate-300" />
                          <span className="text-slate-600">{row.unit_name}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <Wrench className="h-3 w-3 text-slate-400" />
                          <span>{row.template_name}</span>
                          <Badge className="bg-red-200 text-red-800">{row.status}</Badge>
                        </div>
                        <div className="flex items-center gap-4 text-xs text-red-600 mt-2">
                          <span className="font-medium">
                            {row.days_overdue} days overdue
                          </span>
                          <span>
                            Was due: {row.next_due_date ? new Date(row.next_due_date).toLocaleDateString() : "-"}
                          </span>
                        </div>
                      </div>
                      <div className="text-right text-sm">
                        {row.assigned_contractor_name ? (
                          <span className="text-slate-600">{row.assigned_contractor_name}</span>
                        ) : (
                          <Badge className="bg-red-100 text-red-700">Unassigned</Badge>
                        )}
                        {row.last_job_reference && (
                          <div className="text-xs text-slate-500 mt-1">Last: {row.last_job_reference}</div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
                {(overdue?.rows || []).length === 0 && (
                  <div className="text-center py-8 text-green-500">
                    <CheckCircle2 className="h-12 w-12 mx-auto mb-2 opacity-50" />
                    <p>No overdue PPM items - great work!</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
